import { useDispatch, useSelector } from 'react-redux'
import { getEntityType } from '../store/entity-type/actions'
import { useEffect, useMemo, useState, useCallback } from 'react'

export function resolveEntityType(
  entityTypes,
  { route, entity, name = null, id = null }
) {
  if (entity?.entity_type_id) {
    return entityTypes?.find(
      (entityType) => entityType.id === entity.entity_type_id
    )
  }
  if (id) {
    return entityTypes?.find((entityType) => entityType.id === id)
  }
  if (route) {
    if (!route.params?.type) {
      return null
    }
    return entityTypes?.find((t) => t.name === route.params?.type)
  }
  if (name) {
    return entityTypes?.find((t) => t.name === name)
  }

  return null
}

export const useDiscoverEntityEntityType = (
  route,
  entity,
  name = null,
  id = null,
  forceFetch = false
) => {
  const dispatch = useDispatch()
  const entityTypes = useSelector((AppState) => AppState.entityTypes.data)

  const tenantId = useSelector(
    (AppState) => AppState.users.user?.tenant_record?.id
  )
  const [didFetch, setDidFetch] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [wasTenantId, setWasTenantId] = useState(tenantId)
  const entityType = useMemo(() => {
    return resolveEntityType(entityTypes, { route, entity, name, id })
  }, [entityTypes, entity?.entity_type_id, route?.params?.type, name, id])

  useEffect(() => {
    if (!entityType) {
      return
    }
    setDidFetch(false)
  }, [entityType?.id])

  useEffect(() => {
    if (tenantId && didFetch && tenantId !== wasTenantId) {
      setWasTenantId('' + tenantId)
      refetch()
    }
  }, [tenantId, didFetch, wasTenantId])

  const refetch = useCallback(() => {
    const fetchId =
      entity?.entity_type_id || id || (route && route?.params?.type) || name
    if (fetchId) {
      setDidFetch(true)
      setIsFetching(true)
      dispatch(getEntityType(fetchId, undefined, { read: true })).finally(
        () => {
          setIsFetching(false)
        }
      )
    }
  }, [entity, id, route, name, dispatch])

  useEffect(() => {
    if (
      !entityType ||
      !entityType.fields ||
      forceFetch ||
      !entityTypes?.length
    ) {
      if (didFetch) {
        return
      }
      refetch()
    }
  }, [
    entityType?.id,
    id,
    name,
    forceFetch,
    didFetch,
    entityTypes?.length,
    entity?.entity_type_id,
    route
  ])

  return { entityType, isFetching, didFetch }
}

export function useEntityTypeResolver() {
  const dispatch = useDispatch()
  const entityTypes = useSelector((AppState) => AppState.entityTypes.data)

  const resolver = useCallback(
    (route, entity, name = null, id = null, forceFetch = false) => {
      const entityType = resolveEntityType(entityTypes, {
        route,
        entity,
        name,
        id
      })
      if (!entityType || forceFetch) {
        const fetchId =
          entity?.entity_type_id || id || (route && route?.params?.type) || name
        if (fetchId) {
          dispatch(getEntityType(fetchId, undefined, { read: true }))
        }
      }
      return entityType
    },
    [entityTypes, dispatch]
  )

  return resolver
}
